import { gql } from '@apollo/client';

// Event and Role queries - Fixed to match backend schema
export const GET_ROLES = gql`
    query GetEventRoles {
        event {
            serial
            id
            name
            created
            roles
        }
    }
`;

export const GET_ME = gql`
    query GetMe {
        me {
            id
            permissions
        }
    }
`;

// Dashboard Overview - matches backend schema
export const GET_DASHBOARD_OVERVIEW = gql`
    query GetDashboardOverview($eventId: String!) {
        dashboardOverview(eventId: $eventId) {
            totalRegistrations
            totalPrints
            totalScans
            uniqueVisitors
            activeUsers
            conversionRate
            avgSessionDuration
            recentActivity {
                id
                type
                user
                timestamp
                location
            }
            performanceMetrics {
                responseTime
                uptime
                throughput
                errorRate
            }
            insights {
                type
                title
                description
                value
                trend
                color
            }
        }
    }
`;

// Enhanced Dashboard Overview with detailed breakdowns
export const GET_ENHANCED_DASHBOARD_OVERVIEW = gql`
    query GetEnhancedDashboardOverview($eventId: String!, $dateRange: String!) {
        analyticsOverview(eventId: $eventId, dateRange: $dateRange) {
            totalRegistrations
            totalScans
            totalPrints
            uniqueVisitors
            conversionRate
            avgSessionDuration
            topCountries {
                country
                count
                percentage
            }
            roleBreakdown {
                role
                count
                percentage
                growth
            }
            timeSeriesData {
                date
                registrations
                scans
                prints
                visitors
            }
            performanceMetrics {
                responseTime
                uptime
                throughput
                errorRate
            }
            insights {
                type
                title
                description
                value
                trend
                color
            }
        }
    }
`;

// Role-specific data query
export const GET_ROLE_DETAILS = gql`
    query GetRoleDetails($eventId: String!, $role: String!, $dateRange: String) {
        roleDetails(eventId: $eventId, role: $role, dateRange: $dateRange) {
            role
            totalRegistrations
            allowedRegistrations
            emailVerificationEnabled
            paymentEnabled
            statusBreakdown {
                pending
                accept
                reject
            }
            paymentBreakdown {
                initiated
                success
                rejected
            }
            recentRegistrations {
                id
                name
                email
                status
                paymentStatus
                createdAt
            }
        }
    }
`;

// Real-time Statistics
export const GET_REAL_TIME_STATS = gql`
    query GetRealTimeStats($eventId: String!) {
        realTimeStats(eventId: $eventId) {
            activeUsers
            currentScans
            recentActivity {
                id
                type
                user
                timestamp
                location
            }
            systemHealth {
                status
                cpu
                memory
                connections
            }
        }
    }
`;

// Analytics Data - matches backend schema
export const GET_ANALYTICS_DATA = gql`
    query GetAnalyticsData($eventId: String!, $dateRange: String!) {
        analyticsOverview(eventId: $eventId, dateRange: $dateRange) {
            totalRegistrations
            totalScans
            totalPrints
            uniqueVisitors
            conversionRate
            avgSessionDuration
            topCountries {
                country
                count
                percentage
            }
            roleBreakdown {
                role
                count
                percentage
                growth
            }
            timeSeriesData {
                date
                registrations
                scans
                prints
                visitors
            }
            performanceMetrics {
                responseTime
                uptime
                errorRate
                throughput
            }
            insights {
                type
                title
                description
                value
                trend
                color
            }
        }
    }
`;

// Footfall queries - matches existing backend
export const GET_FOOTFALL_COUNT = gql`
    query GetFootfallCount($eventId: String!) {
        getGateScannerData(eventId: $eventId) {
            totalCount
            gates {
                id
                name
                totalCount
                roleBreakdown {
                    role
                    count
                }
                dayWiseData {
                    date
                    count
                }
            }
            dayWiseData {
                date
                count
            }
        }
    }
`;

export const GET_FOOTFALL_DATA = gql`
    query GetFootfallData($eventId: String!, $limit: Int, $offset: Int) {
        getGateScannerData(eventId: $eventId) {
            gates {
                id
                name
                totalCount
            }
            totalCount
        }
    }
`;

// Print queries - matches existing backend
export const GET_PRINTS_COUNT = gql`
    query GetPrintsCount($min: String, $max: String, $role: String) {
        printsCount(min: $min, max: $max, role: $role) {
            role
            total
        }
        printsMinTime
        printsMaxTime
    }
`;

export const GET_PRINTS_DATA = gql`
    query GetPrintsData($conditions: InputConditions, $eventId: String!, $role: ROLE!, $getData: [String]) {
        onsitePrint(conditions: $conditions, eventId: $eventId, role: $role, getData: $getData) {
            id
            serial
            created
            staffId
            role
            direction
            data
        }
    }
`;

// Registration queries - matches existing backend
export const GET_REGISTRATION_DATA = gql`
    query GetRegistrationData($conditions: InputConditions, $eventId: String!, $role: ROLE!) {
        applicants(conditions: $conditions, eventId: $eventId, role: $role) {
            id
            serial
            created
            regLink
            data
        }
    }
`;

export const GET_REGISTRATION_STATS = gql`
    query GetRegistrationStats($conditions: InputConditions, $eventId: String!, $role: ROLE!) {
        applicantsCount(conditions: $conditions, eventId: $eventId, role: $role)
    }
`;

// System Health
export const GET_SYSTEM_HEALTH = gql`
    query GetSystemHealth {
        systemHealth {
            status
            cpu
            memory
            connections
        }
    }
`;

// Bulk operations
export const GET_BULK_PRINT_STATUS = gql`
    query GetBulkPrintStatus($eventId: String!) {
        bulkPrintStatus(eventId: $eventId) {
            status
            progress
            total
            completed
            failed
            startTime
            estimatedCompletion
        }
    }
`;

// Lead generation queries
export const GET_LEAD_GEN_DATA = gql`
    query GetLeadGenData($eventId: String!, $exhibitorId: String) {
        standsByEventId(eventId: $eventId)
        ScanDataByStandId(eventId: $eventId, standId: $exhibitorId)
    }
`;

// Role permissions and features query
export const GET_ROLE_PERMISSIONS = gql`
    query GetRolePermissions($eventId: String!) {
        rolePermissions(eventId: $eventId) {
            role
            features
            emailVerificationEnabled
            paymentEnabled
            allowedRegistrations
        }
    }
`;

// Real registration data query
export const GET_REAL_REGISTRATIONS = gql`
    query GetRealRegistrations($eventId: String!, $dateRange: String!) {
        realRegistrations(eventId: $eventId, dateRange: $dateRange) {
            totalRegistrations
            roleBreakdown {
                role
                count
                percentage
                emailVerificationEnabled
                paymentEnabled
                statusBreakdown {
                    pending
                    accept
                    reject
                }
                paymentBreakdown {
                    initiated
                    success
                    rejected
                }
            }
            countryBreakdown {
                country
                count
                percentage
            }
            timeSeriesData {
                date
                registrations
            }
        }
    }
`;

// Real print and scan data query
export const GET_REAL_PRINT_SCAN_DATA = gql`
    query GetRealPrintScanData($eventId: String!, $dateRange: String!) {
        realPrintScanData(eventId: $eventId, dateRange: $dateRange) {
            totalPrints
            totalScans
            printBreakdown {
                total
                unique
                duplicate
            }
            scanBreakdown {
                total
                unique
                duplicate
            }
            timeSeriesData {
                date
                prints
                scans
            }
        }
    }
`;

// Comprehensive dashboard query
export const GET_COMPREHENSIVE_DASHBOARD = gql`
    query GetComprehensiveDashboard(
        $eventId: String!
        $dateRange: String
        $customStartDate: String
        $customEndDate: String
    ) {
        comprehensiveDashboard(
            eventId: $eventId
            dateRange: $dateRange
            customStartDate: $customStartDate
            customEndDate: $customEndDate
        ) {
            eventInfo {
                id
                name
                roles
                location
                startAt
                endAt
            }
            registrations
            footfall {
                total
                entry
                exit
                prints
                uniqueVisitors
                uniquePrintVisitors
                uniqueEntryVisitors
                uniqueExitVisitors
                rawData
                printDataWithUsers
                entryDataWithUsers
                exitDataWithUsers
                allFootfallWithUsers
                failedFootfall {
                    total
                    entry
                    exit
                    prints
                    byReason
                    rawData
                    printDataWithUsers
                    entryDataWithUsers
                    exitDataWithUsers
                    allFailedFootfallWithUsers
                }
            }
            transactions {
                total
                totalAmount
                byStatus
            }
            emails {
                total
                byReason
            }
            countries {
                total
                totalCountries
                countries
                breakdown {
                    country
                    count
                }
                roleWiseBreakdown
            }
            stands
            meetingRequests
            promo
            importHistory
        }
    }
`;

// Get role-specific data
export const GET_ROLE_DATA = gql`
    query GetRoleData(
        $eventId: String!
        $role: String!
        $dateRange: String
        $customStartDate: String
        $customEndDate: String
    ) {
        getRoleData(
            eventId: $eventId
            role: $role
            dateRange: $dateRange
            customStartDate: $customStartDate
            customEndDate: $customEndDate
        ) {
            role
            totalRecords
            columns
            data
            formFields
        }
    }
`;

// Get all roles data for export (Total Registration)
export const GET_ALL_ROLES_DATA = gql`
    query GetAllRolesData($eventId: String!, $dateRange: String, $customStartDate: String, $customEndDate: String) {
        getAllRolesData(
            eventId: $eventId
            dateRange: $dateRange
            customStartDate: $customStartDate
            customEndDate: $customEndDate
        )
    }
`;

// Get form features for a specific role
export const GET_FORM_FEATURES = gql`
    query GetFormFeatures($eventId: String!, $role: ROLE!) {
        form(eventId: $eventId, role: $role) {
            features
        }
    }
`;

// Export utilities with meaningful names
export const DASHBOARD_QUERIES = {
    GET_ROLES,
    GET_ME,
    GET_DASHBOARD_OVERVIEW,
    GET_REAL_TIME_STATS,
    GET_ANALYTICS_DATA,
    GET_SYSTEM_HEALTH,
};

export const DATA_QUERIES = {
    GET_FOOTFALL_COUNT,
    GET_FOOTFALL_DATA,
    GET_PRINTS_COUNT,
    GET_PRINTS_DATA,
    GET_REGISTRATION_DATA,
    GET_REGISTRATION_STATS,
};

export const GET_CREW_BREAKDOWN = gql`
    query GetCrewBreakdown(
        $eventId: String!
        $footfallType: String!
        $dateRange: String
        $customStartDate: String
        $customEndDate: String
    ) {
        getCrewBreakdown(
            eventId: $eventId
            footfallType: $footfallType
            dateRange: $dateRange
            customStartDate: $customStartDate
            customEndDate: $customEndDate
        ) {
            crewId
            crewName
            totalCount
            uniqueCount
        }
    }
`;

export const GET_CREW_ENTRIES = gql`
    query GetCrewEntries(
        $eventId: String!
        $crewId: String!
        $footfallType: String!
        $dateRange: String
        $customStartDate: String
        $customEndDate: String
    ) {
        getCrewEntries(
            eventId: $eventId
            crewId: $crewId
            footfallType: $footfallType
            dateRange: $dateRange
            customStartDate: $customStartDate
            customEndDate: $customEndDate
        ) {
            entries {
                id
                firstName
                lastName
                name
                email
                mobile
                created
            }
            total
        }
    }
`;

export const OPERATIONAL_QUERIES = {
    GET_BULK_PRINT_STATUS,
    GET_LEAD_GEN_DATA,
    GET_CREW_BREAKDOWN,
    GET_CREW_ENTRIES,
};

// Legacy export for compatibility
export const QUERIES = {
    ...DASHBOARD_QUERIES,
    ...DATA_QUERIES,
    ...OPERATIONAL_QUERIES,
};

export default QUERIES;
